import React, { useEffect } from 'react';
import { Typography, Price } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from 'common/hooks';
import { ProductMainInfoProps } from 'features/product-info/types';
import { useOptimizedImages } from 'common/hooks/use-optimized-images';
import { ImagePlaceholder, ImageFallback } from '@navi-toolbox/ui-components';
import {
  priceStub,
  productCard,
  productCardColumn,
  productCardContent,
  productCardImage,
  productCardInfo,
  productCardName,
  productCardPlenty,
  productCardPlentyPrice,
  productCardPrice,
  productCardPriceWrapper,
} from './styles';
import { useDispatch } from 'react-redux';
import { PRODUCT_IN_SET_CLICK, PRODUCT_IN_SET_VIEW } from 'features/related-products/actions';
import { ProductInSetClickType } from 'features/ga-events/messages/sets/types';
import { useInView } from 'react-cool-inview';

export const DATA_QA_SET_PRODUCT_CARD = 'set-product-card';
const size = 300;
const stub = '';

type Props = {
  quantity: number;
  index: number;
} & ProductMainInfoProps;

export function ProductCard(props: Props) {
  const { displayedName, eligibility, mediaMainPhoto, price, productId, productPageName, quantity, index } =
    props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { mobile } = useBreakpoints() || {
    mobile: true,
  };
  const { observe, inView } = useInView();

  const plenty = quantity > 1 ? `${quantity} ${price.main_uom_rus} по` : '';
  const currentPrice = quantity > 1 ? price.main_price * quantity : price.main_price;

  const src = useOptimizedImages(
    mediaMainPhoto.desktop ?? mediaMainPhoto,
    {
      size: { w: `${size}` },
    },
    {
      c: 'pad',
      b: 'white',
    },
  );

  const isEligible = eligibility?.webEligible === true;

  const sendGaEvent = (elementName: ProductInSetClickType['elementName']) => {
    dispatch(
      PRODUCT_IN_SET_CLICK({
        elementName,
        productInfo: props,
        index: index + 1,
      }),
    );
  };

  useEffect(() => {
    if (inView) {
      dispatch(
        PRODUCT_IN_SET_VIEW({
          productInfo: props,
          index: index + 1,
        }),
      );
    }
  }, [dispatch, inView]);

  return (
    <a
      ref={observe}
      href={`/product/${productPageName}/`}
      className={productCard}
      data-qa={DATA_QA_SET_PRODUCT_CARD}
    >
      <ImagePlaceholder
        onClick={() => sendGaEvent('productPhotoLink')}
        src={src.size.x || ''}
        alt={displayedName || ''}
        className={productCardImage}
        fallback={
          <ImageFallback alt={t('ui.image.alt')} className={productCardImage} width={size} height={size} />
        }
      />
      <div className={productCardContent} onClick={() => sendGaEvent('productNameLink')}>
        <Typography size="caption" variant="minor" color="text-minor">
          {t('product-card.product-id')} {productId}
        </Typography>
        <div className={productCardInfo}>
          <Typography
            className={productCardName}
            size={mobile ? 'body_long' : 'body_short'}
            variant={mobile ? 's' : 'm'}
            color="text-primary"
          >
            {displayedName}
          </Typography>
          <div className={productCardColumn}>
            {isEligible ? (
              <div className={productCardPriceWrapper}>
                <Price
                  className={productCardPrice}
                  fraction
                  size={mobile ? 's' : 'l'}
                  value={currentPrice}
                  variant="medium"
                  inline
                />
                {plenty ? (
                  <span className={productCardPlenty}>
                    <Typography color="text-secondary" size="caption_accent" variant="minor">
                      {plenty}
                    </Typography>
                    &nbsp;
                    <Price
                      className={productCardPlentyPrice}
                      fraction
                      size="xs"
                      value={price.main_price}
                      variant="medium"
                      color="text-secondary"
                      inline
                    />
                  </span>
                ) : (
                  <div className={priceStub}>{stub}</div>
                )}
              </div>
            ) : (
              <>
                <Typography size="body_accent" variant="s" color="attention-primary">
                  {t('product-info.set.out-of-stock-product')}
                </Typography>
                <div className={priceStub}>{stub}</div>
              </>
            )}
          </div>
        </div>
      </div>
    </a>
  );
}
